import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import Loading from "react-fullscreen-loading";
import Select from "react-select";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-number-input";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import UserDashboardLayout from "../../../Components/UserDashboardLayout/UserDashboardLayout";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import { useAuth } from "../../../authentication/AuthContext";
import { apiErrorMessage } from "../../../utils/apiErrors";
import { saveCredential } from "../../../utils/saveCredential";
import "react-phone-number-input/style.css";
import "./Profile.css";

const authHeader = () => ({ headers: { "access-token": localStorage.getItem("accessToken") } });

// Where a user reviews and corrects what they entered at signup — and, because
// signup's second step is skippable, where they finish what they skipped. The
// dashboard's setup card links straight here for exactly that reason.
//
// Password changes live on the same page: it is where people look for them, and
// splitting "your account" across two screens for one form isn't worth a route.
//
// Serves all three portals. Admins and resellers are rows in the same Users
// table and their accounts need the same corrections and password changes, so
// they reach this same page from their own profile dropdown — the form is
// identical, only the chrome around it follows the portal. It is reached at the
// shared /Profile rather than under /User for that reason, and is left in this
// folder only because that is where it was born.
export default function Profile() {
  const navigate = useNavigate();
  const { verified, userType, setProfileComplete } = useAuth();

  // Resellers share the admin chrome, the same way ResellerDashboard does.
  const DashboardLayout = userType === "user" ? UserDashboardLayout : AdminDashboardLayout;

  // Email verification is a customer-portal concern: it is signup that leaves an
  // account unverified, and /User/VerifyEmail — the only way to act on the
  // prompt — is a customer route. Showing an admin a badge it cannot clear would
  // be a dead end, so neither the badge nor the link is rendered for them.
  const showVerification = userType === "user";
  const countryOptions = useMemo(() => countryList().getData(), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [organization, setOrganization] = useState("");
  const [country, setCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errors, setErrors] = useState({});

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [pwErrors, setPwErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/profile`, authHeader());
        if (res.data?.status === 1) {
          applyProfile(res.data.profile);
        } else {
          toast.error(res.data?.message || "Could not load your profile.");
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Unable to load your profile.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyProfile = (p) => {
    setProfile(p);
    setFname(p.fname || "");
    setLname(p.lname || "");
    setOrganization(p.organization || "");
    setPhoneNumber(p.phone_number || "");
    // The country is stored as its label, which is what signup sent; match it
    // back to an option so the select shows a selection rather than a blank.
    setCountry(countryOptions.find(o => o.label === p.country) || null);
  };

  const validateProfile = () => {
    const next = {};
    if (fname.trim().length < 2 || fname.trim().length > 30) {
      next.fname = "First name is required (2-30 characters)";
    }
    if (lname.trim().length < 2 || lname.trim().length > 30) {
      next.lname = "Last name is required (2-30 characters)";
    }
    // The optional three stay optional — a user who skipped them at signup can
    // keep skipping them. They're only validated once something is typed.
    if (organization && (organization.trim().length < 3 || organization.trim().length > 50)) {
      next.organization = "Organization must be 3-50 characters";
    }
    if (phoneNumber && phoneNumber.length < 6) {
      next.phoneNumber = "Please enter a valid phone number";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;

    setSaving(true);
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/profile`, {
        fname: fname.trim(),
        lname: lname.trim(),
        organization: organization.trim(),
        country: country?.label || "",
        phone_number: phoneNumber || "",
      }, authHeader());

      if (res.data?.status === 1) {
        applyProfile(res.data.profile);
        // The server recomputes completeness from what it stored; take its word
        // rather than assuming this save finished the job.
        setProfileComplete(res.data.profileComplete);
        toast.success("Profile updated.");
      } else {
        toast.error(res.data?.message || "Could not save your profile.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Unable to save your profile."));
    } finally {
      setSaving(false);
    }
  };

  const validatePassword = () => {
    const next = {};
    if (newPassword.length < 6 || newPassword.length > 20) {
      next.newPassword = "New password must be 6-20 characters";
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(newPassword)) {
      next.newPassword = "Include both letters and numbers";
    }
    if (confirmPassword !== newPassword) {
      next.confirmPassword = "Passwords do not match";
    }
    setPwErrors(next);
    return Object.keys(next).length === 0;
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setChangingPassword(true);
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/profile/password`, {
        new_password: newPassword,
      }, authHeader());

      if (res.data?.status === 1) {
        // Hand the NEW password to the password manager under the same email,
        // which updates the stored entry rather than adding a second one — so
        // the next sign-in autofills what actually works. Done before the
        // fields are cleared, and awaited so the prompt isn't cut short.
        await saveCredential({ email: profile?.email, password: newPassword });

        toast.success("Password updated.");
        setNewPassword("");
        setConfirmPassword("");
        setPwErrors({});
      } else if (res.data?.status === -3) {
        // Server-side reuse check, which needs the stored hash.
        setPwErrors({ newPassword: res.data.message });
      } else {
        toast.error(res.data?.message || "Could not update your password.");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Unable to update your password."));
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div>
      <DashboardLayout />
      <Loading loading={loading || saving || changingPassword} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="profile-page">

              <div className="profile-panel">
                <div className="profile-panel-head">
                  <h2>My profile</h2>
                  <p>The details you gave us when you signed up. Update them any time.</p>
                </div>

                <form onSubmit={saveProfile}>
                  <div className="profile-grid">
                    <div className="profile-field">
                      <label htmlFor="fname">First name</label>
                      <input
                        id="fname"
                        className={`profile-input${errors.fname ? " is-invalid" : ""}`}
                        type="text"
                        autoComplete="given-name"
                        value={fname}
                        maxLength={30}
                        onChange={(e) => { setFname(e.target.value); setErrors(p => ({ ...p, fname: "" })); }}
                      />
                      {errors.fname && <span className="profile-error">{errors.fname}</span>}
                    </div>

                    <div className="profile-field">
                      <label htmlFor="lname">Last name</label>
                      <input
                        id="lname"
                        className={`profile-input${errors.lname ? " is-invalid" : ""}`}
                        type="text"
                        autoComplete="family-name"
                        value={lname}
                        maxLength={30}
                        onChange={(e) => { setLname(e.target.value); setErrors(p => ({ ...p, lname: "" })); }}
                      />
                      {errors.lname && <span className="profile-error">{errors.lname}</span>}
                    </div>

                    <div className="profile-field is-full">
                      <label htmlFor="email">
                        Email
                        {showVerification && (
                          <>
                            <span className={`profile-badge ${verified ? "is-verified" : "is-unverified"}`}>
                              {verified ? "Verified" : "Not verified"}
                            </span>
                            {!verified && (
                              <button
                                type="button"
                                className="profile-verify-link"
                                onClick={() => navigate("/User/VerifyEmail")}
                              >
                                Verify now
                              </button>
                            )}
                          </>
                        )}
                      </label>
                      <input
                        id="email"
                        className="profile-input"
                        type="email"
                        value={profile?.email || ""}
                        readOnly
                      />
                      {/* Email is the login identity and the anchor for
                          verification, so changing it here would silently
                          invalidate both. It needs its own re-verification
                          flow, which doesn't exist yet. */}
                      <span className="profile-hint">
                        Your email is your sign-in name. Contact support if you need it changed.
                      </span>
                    </div>

                    <div className="profile-field is-full">
                      <label htmlFor="organization">
                        Organization
                      </label>
                      <input
                        id="organization"
                        className={`profile-input${errors.organization ? " is-invalid" : ""}`}
                        type="text"
                        autoComplete="organization"
                        value={organization}
                        maxLength={50}
                        onChange={(e) => { setOrganization(e.target.value); setErrors(p => ({ ...p, organization: "" })); }}
                      />
                      {errors.organization && <span className="profile-error">{errors.organization}</span>}
                    </div>

                    <div className="profile-field">
                      <label htmlFor="country">
                        Country
                      </label>
                      <Select
                        inputId="country"
                        classNamePrefix="react-select"
                        placeholder="Select country"
                        options={countryOptions}
                        value={country}
                        isClearable
                        onChange={(option) => setCountry(option)}
                      />
                    </div>

                    <div className="profile-field">
                      <label htmlFor="phoneNumber">
                        Phone number
                      </label>
                      <PhoneInput
                        international
                        id="phoneNumber"
                        name="phone_number"
                        autoComplete="tel"
                        placeholder="e.g. +1 234 567 890"
                        value={phoneNumber}
                        onChange={(value) => { setPhoneNumber(value || ""); setErrors(p => ({ ...p, phoneNumber: "" })); }}
                      />
                      {errors.phoneNumber && <span className="profile-error">{errors.phoneNumber}</span>}
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button type="submit" className="profile-btn" disabled={saving}>
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </form>
              </div>

              <div className="profile-panel">
                <div className="profile-panel-head">
                  <h2>Change password</h2>
                  <p>Pick a new password — you're already signed in, so that's all we need.</p>
                </div>

                <form onSubmit={changePassword}>
                  <div className="profile-grid">
                    {/* The account this password belongs to. A password manager
                        will not attribute a new password without a username
                        field in the SAME form, so this is what makes the
                        browser offer to update the saved entry instead of
                        creating a second, nameless one. Read-only and visible
                        rather than hidden — it also answers "which account am I
                        changing?", which is worth a row on its own. */}
                    <div className="profile-field is-full">
                      <label htmlFor="passwordAccount">Account</label>
                      <input
                        id="passwordAccount"
                        className="profile-input"
                        type="text"
                        name="username"
                        autoComplete="username"
                        value={profile?.email || ""}
                        readOnly
                      />
                    </div>

                    <div className="profile-field">
                      <label htmlFor="newPassword">New password</label>
                      <div className="profile-password-wrap">
                        <input
                          id="newPassword"
                          className={`profile-input${pwErrors.newPassword ? " is-invalid" : ""}`}
                          type={showNew ? "text" : "password"}
                          autoComplete="new-password"
                          value={newPassword}
                          maxLength={20}
                          onChange={(e) => { setNewPassword(e.target.value); setPwErrors(p => ({ ...p, newPassword: "" })); }}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowNew(!showNew)}
                          aria-label={showNew ? "Hide password" : "Show password"}
                        >
                          {showNew ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {pwErrors.newPassword
                        ? <span className="profile-error">{pwErrors.newPassword}</span>
                        : <span className="profile-hint">6-20 characters, with letters and numbers.</span>}
                    </div>

                    <div className="profile-field">
                      <label htmlFor="confirmPassword">Confirm new password</label>
                      <input
                        id="confirmPassword"
                        className={`profile-input${pwErrors.confirmPassword ? " is-invalid" : ""}`}
                        type={showNew ? "text" : "password"}
                        autoComplete="new-password"
                        value={confirmPassword}
                        maxLength={20}
                        onChange={(e) => { setConfirmPassword(e.target.value); setPwErrors(p => ({ ...p, confirmPassword: "" })); }}
                      />
                      {pwErrors.confirmPassword && <span className="profile-error">{pwErrors.confirmPassword}</span>}
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button type="submit" className="profile-btn" disabled={changingPassword}>
                      {changingPassword ? "Updating…" : "Update password"}
                    </button>
                  </div>
                </form>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
